import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getInventoryCount, recordCount, finalizeInventoryCount, type InventoryDifferenceReason } from '../../api/stocuri'
import { errorMessage } from '../../api/client'

const REASON_LABEL: Record<InventoryDifferenceReason, string> = { Scrap: 'Scrap', EroareNumarare: 'Eroare de numărare', Altul: 'Altul' }

export default function InventoryCountDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const open = id !== null
  const qc = useQueryClient()
  const { data: detail } = useQuery({ queryKey: ['stocuri', 'inventar', id], queryFn: () => getInventoryCount(id!), enabled: open })
  const [inputs, setInputs] = useState<Record<string, { counted: string; reason: InventoryDifferenceReason | '' }>>({})
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['stocuri', 'inventar'] })

  const recordMutation = useMutation({
    mutationFn: (lineId: string) => {
      const input = inputs[lineId];
      return recordCount(id!, lineId, { countedQuantity: Number(input.counted), differenceReason: input.reason || null })
    },
    onSuccess: invalidate,
    onError: (e) => setError(errorMessage(e)),
  })
  const finalizeMutation = useMutation({
    mutationFn: () => finalizeInventoryCount(id!),
    onSuccess: () => { invalidate(); onClose() },
    onError: (e) => setError(errorMessage(e)),
  })

  const isDraft = detail?.status === 'Draft'
  const allCounted = detail?.lines.every((l) => l.countedQuantity !== null) ?? false

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Numărătoare {detail?.number} — {detail?.warehouseName}</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Articol</TableCell><TableCell>Zonă</TableCell><TableCell>Lot</TableCell>
              <TableCell align="right">Așteptat</TableCell><TableCell align="right">Numărat</TableCell>
              <TableCell>Motiv diferență</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {detail?.lines.map((l) => {
              const input = inputs[l.id] ?? { counted: l.countedQuantity != null ? String(l.countedQuantity) : '', reason: l.differenceReason ?? '' }
              const differs = input.counted !== '' && Number(input.counted) !== l.expectedQuantity
              return (
                <TableRow key={l.id}>
                  <TableCell>{l.articleCode} — {l.articleDescription}</TableCell>
                  <TableCell>{l.zoneName ?? '—'}</TableCell>
                  <TableCell>{l.lotNumber ?? '—'}</TableCell>
                  <TableCell align="right">{l.expectedQuantity}</TableCell>
                  <TableCell align="right">
                    {isDraft ? (
                      <TextField size="small" type="number" value={input.counted}
                        onChange={(e) => setInputs((prev) => ({ ...prev, [l.id]: { ...input, counted: e.target.value } }))} sx={{ width: 110 }} />
                    ) : (l.countedQuantity ?? '—')}
                  </TableCell>
                  <TableCell>
                    {isDraft && differs ? (
                      <TextField select size="small" value={input.reason} sx={{ width: 160 }}
                        onChange={(e) => setInputs((prev) => ({ ...prev, [l.id]: { ...input, reason: e.target.value as InventoryDifferenceReason } }))}>
                        <MenuItem value="">—</MenuItem>
                        {Object.entries(REASON_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                      </TextField>
                    ) : (l.differenceReason ? <Chip size="small" label={REASON_LABEL[l.differenceReason]} /> : null)}
                  </TableCell>
                  <TableCell>
                    {isDraft && (
                      <Button size="small" disabled={!input.counted || (differs && !input.reason) || recordMutation.isPending}
                        onClick={() => recordMutation.mutate(l.id)}>
                        Salvează
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
        {isDraft && (
          <Button variant="contained" disabled={!allCounted || finalizeMutation.isPending} onClick={() => finalizeMutation.mutate()}
            startIcon={finalizeMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Finalizează
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
